import { format, subDays } from 'date-fns';
import type { Booking, User, Space, Payment } from '@/lib/types';
import type { AnalyticsDataset } from '@/lib/types/analytics';

export interface KPIMetrics {
  utilization: number;
  bookingCount: number;
  activeUsers: number;
  revenue: number;
  busiestTime: string;
}

export interface ChartData {
  name: string;
  value: number;
  [key: string]: string | number;
}

/**
 * Analytics calculator
 */
export class AnalyticsCalculator {
  private timeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  private getDateRange(days: number): Date[] {
    const dates: Date[] = [];
    const now = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      dates.push(subDays(now, i));
    }
    
    return dates;
  }

  private getBookingDuration(booking: Booking): number {
    const start = this.timeToMinutes(booking.startTime);
    const end = this.timeToMinutes(booking.endTime);
    return (end - start) / 60; // Return hours
  }

  /**
   * Calculate KPI metrics with simplified logic
   */
  calculateKPIs(data: AnalyticsDataset): KPIMetrics {
    const { bookings, spaces, payments } = data;
    
    // Simple calculations without complex conditional logic
    const totalPossibleHours = spaces.length * 7 * 12; // 7 days * 12 hours
    const bookedHours = bookings.reduce((sum, booking) => 
      sum + this.getBookingDuration(booking), 0
    );
    
    const utilization = totalPossibleHours > 0 ? (bookedHours / totalPossibleHours) * 100 : 0;
    const activeUsers = new Set(bookings.map(b => b.userUid)).size;
    const revenue = payments
      .filter(p => p.status === 'Paid')
      .reduce((sum, p) => sum + p.amount, 0);

    const busiestTime = this.findBusiestTime(bookings);

    return {
      utilization: Math.round(utilization),
      bookingCount: bookings.length,
      activeUsers,
      revenue: Math.round(revenue),
      busiestTime,
    };
  }

  /**
   * Generate daily booking counts
   */
  generateDailyBookings(bookings: Booking[], days = 7): ChartData[] {
    const dates = this.getDateRange(days);
    
    return dates.map(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const dayBookings = bookings.filter(b => b.date === dateStr);
      
      return {
        name: format(date, 'MMM d'),
        value: dayBookings.length,
      };
    });
  }

  /**
   * Generate utilization by weekday
   */
  generateWeekdayUtilization(bookings: Booking[], spaces: Space[]): ChartData[] {
    const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const totalPossiblePerDay = spaces.length * 12; // 12 hours per day
    
    return weekdays.map((day, index) => {
      const dayBookings = bookings.filter(b => new Date(b.date).getDay() === index);
      const bookedHours = dayBookings.reduce((sum, booking) => 
        sum + this.getBookingDuration(booking), 0
      );
      
      const utilization = totalPossiblePerDay > 0 ? (bookedHours / totalPossiblePerDay) * 100 : 0;
      
      return {
        name: day,
        value: Math.round(utilization),
      };
    });
  }

  /**
   * Generate duration breakdown
   */
  generateDurationBreakdown(bookings: Booking[]): Array<{
    name: string;
    value: number;
    percentage: number;
  }> {
    const durations = new Map<string, number>();
    
    bookings.forEach(booking => {
      const hours = Math.floor(this.getBookingDuration(booking));
      const key = `${hours}h`;
      durations.set(key, (durations.get(key) || 0) + 1);
    });

    const total = bookings.length;
    
    return Array.from(durations.entries())
      .map(([name, value]) => ({
        name,
        value,
        percentage: total > 0 ? Math.round((value / total) * 100) : 0,
      }))
      .sort((a, b) => b.value - a.value);
  }

  /**
   * Generate top users analytics
   */
  generateTopUsers(bookings: Booking[], users: User[], payments: Payment[], limit = 10): Array<{
    name: string;
    bookings: number;
    hours: number;
    revenue: number;
  }> {
    const userStats = new Map<string, { bookings: number; hours: number; revenue: number }>();
    
    // Calculate booking stats
    bookings.forEach(booking => {
      const existing = userStats.get(booking.userUid) || { bookings: 0, hours: 0, revenue: 0 };
      existing.bookings++;
      existing.hours += this.getBookingDuration(booking);
      userStats.set(booking.userUid, existing);
    });

    // Add revenue data
    payments.filter(p => p.status === 'Paid').forEach(payment => {
      const existing = userStats.get(payment.userUid);
      if (existing) {
        existing.revenue += payment.amount;
      }
    });

    // Convert to array with user names
    return Array.from(userStats.entries())
      .map(([userId, stats]) => {
        const user = users.find(u => u.uid === userId);
        return {
          name: user?.displayName || `${user?.firstName} ${user?.lastName}` || 'Unknown User',
          bookings: stats.bookings,
          hours: Math.round(stats.hours * 10) / 10,
          revenue: Math.round(stats.revenue),
        };
      })
      .sort((a, b) => b.bookings - a.bookings)
      .slice(0, limit);
  }

  /**
   * Generate top spaces analytics
   */
  generateTopSpaces(bookings: Booking[], spaces: Space[], limit = 10): Array<{
    space: string;
    bookings: number;
    utilization: number;
    revenue: number;
  }> {
    const spaceStats = new Map<string, { bookings: number; hours: number; revenue: number }>();
    
    bookings.forEach(booking => {
      const existing = spaceStats.get(booking.spaceId) || { bookings: 0, hours: 0, revenue: 0 };
      existing.bookings++;
      existing.hours += this.getBookingDuration(booking);
      existing.revenue += booking.priceAmount || 0;
      spaceStats.set(booking.spaceId, existing);
    });

    const totalPossibleHours = 7 * 12; // 7 days * 12 hours per day
    
    return Array.from(spaceStats.entries())
      .map(([spaceId, stats]) => {
        const space = spaces.find(s => s.id === spaceId);
        const utilization = totalPossibleHours > 0 ? (stats.hours / totalPossibleHours) * 100 : 0;
        
        return {
          space: space?.displayName || 'Unknown Space',
          bookings: stats.bookings,
          utilization: Math.round(utilization),
          revenue: Math.round(stats.revenue),
        };
      })
      .sort((a, b) => b.bookings - a.bookings)
      .slice(0, limit);
  }

  /**
   * Find busiest time slot
   */
  private findBusiestTime(bookings: Booking[]): string {
    const timeSlots = new Map<string, number>();
    
    bookings.forEach(booking => {
      const day = format(new Date(booking.date), 'EEE');
      const time = booking.startTime.slice(0, 5); // HH:mm format
      const key = `${day} ${time}`;
      timeSlots.set(key, (timeSlots.get(key) || 0) + 1);
    });
    
    let busiestTime = 'No data';
    let maxCount = 0;
    
    timeSlots.forEach((count, time) => {
      if (count > maxCount) {
        maxCount = count;
        busiestTime = time;
      }
    });
    
    return busiestTime;
  }
} 
